import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { CredentialResponse } from '@react-oauth/google';
import jwtDecode from 'jwt-decode';

export function initGoogleOneTapClient({ clientId, isLoggedIn, isMobile, cooldownMs = 2 * 60 * 60 * 1000 }: { clientId?: string; isLoggedIn: boolean; isMobile: boolean; cooldownMs?: number }) {
    try {
        if (!clientId || isLoggedIn) return;

        const key = 'oneTapSuppressedUntil';
        const now = Date.now().valueOf();
        const suppressedUntil = Number(localStorage.getItem(key) || 0);
        // console.log('suppressedUntil====', now, suppressedUntil, now < suppressedUntil);
        if (now < suppressedUntil) return;

        const ensureGsiLoaded = (timeoutMs = 10000): Promise<void> => {
            return new Promise((resolve, reject) => {
                if ((window as any).google?.accounts?.id) return resolve();
                
                const timeout = setTimeout(() => {
                    reject(new Error('Google GSI script load timeout'));
                }, timeoutMs);
                
                const s = document.createElement('script');
                s.async = true;
                s.defer = true;
                s.src = 'https://accounts.google.com/gsi/client';
                s.onload = () => {
                    clearTimeout(timeout);
                    resolve();
                };
                s.onerror = () => {
                    clearTimeout(timeout);
                    reject(new Error('Failed to load Google GSI script'));
                };
                (document.head || document.body).appendChild(s);
            });
        };

        const onCredentialResponse = ({ credential }: CredentialResponse) => {
            try {
                if (!credential) return;
                const { email } = jwtDecode<{ email: string }>(credential);
                fetch('/bff-api/login/via-social', {
                    method: 'POST',
                    headers: { 'content-type': 'application/x-www-form-urlencoded' },
                    credentials: 'include',
                    body: new URLSearchParams({ username: email, password: credential, loginPlatform: 'googleplus' }) as any,
                })
                    .then((resp: any) => {
                        // console.log('login resp====', resp);
                        if(resp?.ok) {
                            trackGA4Event({ 
                                event: 'user_login',
                                formName: 'googleOneTap',
                                formStep: 'googleOneTap',
                                formValidation: 'success',
                                userAccountType: 'googleOneTap',
                            } as any);
    
                            setTimeout(() => {
                                window.location.reload()
                            }, 300);
                        }
                    })
                    .catch(() => {});
            } catch (_e) {
                console.log('error====', _e);
            }
        };

        const start = () => {
            const g = (window as any).google?.accounts?.id;
            if (!g) {
                // Limit retries to prevent infinite loops in test environments
                const retryCount = (start as any).retryCount || 0;
                if (retryCount >= 5) { // Max 5 retries = ~1 second
                    console.warn('Google One Tap: GSI not available after retries');
                    return;
                }
                (start as any).retryCount = retryCount + 1;
                setTimeout(start, 200);
                return;
            }
            (start as any).retryCount = 0; // Reset on success

            trackGA4Event({ event: 'google_one_tap_init', userAccountType: 'googleOneTap' } as any);
            g.initialize({ 
                client_id: clientId, 
                callback: onCredentialResponse, 
                use_fedcm_for_prompt: isMobile ? false : true,
                cancel_on_tap_outside: false,
            });
            g.prompt((n: any) => {
                console.log('prompt====', n);
                try {
                    if (n && n.isDisplayMoment && n.isDisplayMoment()) {
                        // console.log('display moment====', n);
                    }
                    if (n && n.isNotDisplayed && n.isNotDisplayed()) {
                        // const reasonNd = n.getNotDisplayedReason && n.getNotDisplayedReason();
                        // console.log('not displayed====', n, reasonNd);
                    }
                    if (n && n.isSkippedMoment && n.isSkippedMoment()) {
                        // const reasonSk = n.getSkippedReason && n.getSkippedReason();
                        // console.log('skipped moment====', n, reasonSk)
                        localStorage.setItem(key, String(Date.now() + cooldownMs));
                    }
                    if (n && n.isDismissedMoment && n.isDismissedMoment()) {
                        // console.log('dismissed====', n);
                    }
                } catch (_e) {
                    console.log('prompt error====', _e);
                }
            });
        };

        const run = () => {
            ensureGsiLoaded()
                .then(start)
                .catch((err) => {
                    // Silently fail in test environments or when script fails to load
                    console.warn('Google One Tap initialization failed:', err);
                });
        };
        if (document.readyState === 'complete') run();
        else window.addEventListener('load', run, { once: true });
    } catch (_e) {
        console.log('init error====', _e);
    }
}

export default initGoogleOneTapClient;


